// src/components/SuperAdminLogin.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, provider } from '../../firebase';
import { signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export default function SuperAdminLogin() {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async () => {
        setLoading(true);
        setError('');

        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            if (!user || !user.email) {
                throw new Error('No email returned from provider.');
            }

            // Normalize email to lower-case (ensure your Firestore doc IDs are stored in lowercase)
            const email = user.email.toLowerCase();

            // NOTE: update the collection name to match your Firestore exactly.
            // If your screenshot shows "Admin" (singular), use 'Admin' here.
            const COLLECTION_NAME = 'Admin'; // <- change to 'Admins' if that's what you use

            const adminRef = doc(db, COLLECTION_NAME, email);
            const snap = await getDoc(adminRef);

            // Check that the doc exists
            if (!snap.exists()) {
                // Sign out any partial auth session for safety
                try { await signOut(auth); } catch (e) { /* ignore signout errors */ }

                setError('Not authorized as super admin.');
                return;
            }

            const data = snap.data() || {};

            // Ensure IsActive === true (strict check)
            if (data.IsActive !== true) {
                try { await signOut(auth); } catch (e) { /* ignore */ }
                setError('Your super-admin access is not active.');
                return;
            }

            // Optional: check role if you want only certain roles
            const allowedRoles = ['admin', 'super-admin']; // edit if you only want 'super-admin'
            if (!data.role || !allowedRoles.includes(String(data.role).toLowerCase())) {
                try { await signOut(auth); } catch (e) { /* ignore */ }
                setError('Your account does not have the required role to access this area.');
                return;
            }

            // All good — redirect to dashboard
            navigate('/dashboard');
            console.log('Super-admin logged in:', email);
        } catch (err) {
            // Friendly error messages for common cases
            let message = 'An error occurred during sign in.';
            if (err?.code === 'auth/popup-closed-by-user' || err?.message?.toLowerCase()?.includes('popup')) {
                message = 'Sign in was cancelled.';
            } else if (err?.message) {
                message = err.message;
            }
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md w-full">
                <h2 className="text-2xl mb-4 font-semibold">Super Admin Login</h2>

                <button
                    onClick={handleLogin}
                    disabled={loading}
                    className={`w-full py-3 rounded-md text-lg font-medium transition-colors duration-300 ${loading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                        } text-white shadow-lg`}
                    aria-busy={loading}
                >
                    {loading ? 'Signing in...' : 'Sign in with Google'}
                </button>

                {error && (
                    <p className="mt-4 text-red-500" role="alert">
                        {error}
                    </p>
                )}
            </div>
        </div>
    );
}
